import axios from 'axios';
import toast from 'react-hot-toast';
import { useAuthStore } from '../stores/authStore';
import { useSessionHealthStore } from './session-health';
import { applyPendingUpdate, showUpdateRequiredToast } from './sw-update';
import { CLIENT_HEADERS, reportIfUpdateRequired } from './client-version';

const api = axios.create({
  baseURL: import.meta.env.VITE_API_URL || '',
  withCredentials: true,
});

let refreshPromise: Promise<string> | null = null;

/**
 * Single-flight refresh: concurrent callers (e.g. multiple pending API
 * requests on app boot, plus the silent-refresh bootstrap hook) all await
 * the same promise instead of each firing their own /auth/refresh call.
 * Audit 2026-04-10 #6 — exported so the bootstrap hook can reuse it.
 */
let refreshAbort: AbortController | null = null;

export async function refreshAccessTokenSingleFlight(): Promise<string> {
  if (!refreshPromise) {
    // The session this refresh belongs to. A logout that lands while the
    // request is in flight must not be undone by its late success.
    const generation = sessionGeneration;
    // Logout aborts the request itself: the generation check below only
    // discards the token, but the browser has by then already applied the
    // response's rotated Set-Cookie, which could overwrite the refresh cookie
    // of a user who signed in right after.
    const abort = new AbortController();
    refreshAbort = abort;
    refreshPromise = axios
      .post(
        `${import.meta.env.VITE_API_URL || ''}/api/v1/auth/refresh`,
        {},
        { withCredentials: true, signal: abort.signal, headers: { ...CLIENT_HEADERS } },
      )
      .then(({ data }) => {
        const newAccessToken = data?.data?.accessToken as string | undefined;
        if (!newAccessToken) {
          throw new Error('Missing access token in refresh response');
        }
        if (generation !== sessionGeneration) {
          // Signed out while the refresh was in flight: discard the token
          // instead of writing it (and running a recovery callback whose
          // profile fetch would set hasSession back to true on a shared PC).
          throw new SessionEndedDuringRefreshError();
        }
        useAuthStore.getState().setAccessToken(newAccessToken);
        markSessionHealthy();
        return newAccessToken;
      })
      .catch((err: unknown) => {
        reportIfUpdateRequired(err);
        if (axios.isCancel(err) && generation !== sessionGeneration) {
          throw new SessionEndedDuringRefreshError();
        }
        throw err;
      })
      .finally(() => {
        refreshPromise = null;
        if (refreshAbort === abort) refreshAbort = null;
      });
  }
  return refreshPromise;
}

// ── Refresh-failure classification ──────────────────────────────────────

interface ApiErrorShape {
  response?: { status?: number; data?: { error?: { code?: string } } };
}

export function apiErrorStatus(err: unknown): number | undefined {
  return (err as ApiErrorShape | null)?.response?.status;
}

export function apiErrorCode(err: unknown): string | undefined {
  return (err as ApiErrorShape | null)?.response?.data?.error?.code;
}

/**
 * Error codes the API uses when the refresh token itself is dead: expired,
 * revoked, replayed (FAMILY_COMPROMISED), never sent (MISSING_TOKEN), or the
 * bearer/origin was rejected outright (AUTH_*). Only these mean "the session
 * is gone, log out". Anything else — no response at all (offline, CORS/CSP
 * block, DNS), 429 from a shared rate-limit bucket, 5xx mid-deploy, or a
 * feature gate such as CONSENT_REQUIRED / MFA_REQUIRED — leaves the session
 * intact.
 */
const DEFINITIVE_AUTH_CODE = /^(AUTH_|MISSING_TOKEN$|FAMILY_COMPROMISED$)/;

export function isDefinitiveAuthFailure(err: unknown): boolean {
  const status = apiErrorStatus(err);
  if (status !== 400 && status !== 401 && status !== 403) return false;
  const code = apiErrorCode(err);
  // A 401 with no structured body (e.g. from a proxy in front of the API)
  // still means the credentials were rejected. 400/403 need an explicit code:
  // 403 is also how consent and MFA gates answer, and those must not log out.
  if (!code) return status === 401;
  return DEFINITIVE_AUTH_CODE.test(code);
}

// ── Session teardown + degraded-mode retry ───────────────────────────────

// Only force a redirect when the user is on a guarded page. Public pages
// (terms, privacy, login, register, join, extension) should surface the
// underlying 401 so the caller can show a real error instead of silently
// bouncing the user to /login (which also breaks "open /terms in a new tab"
// when a stale refresh cookie causes the refresh call to fail in the new tab).
const PUBLIC_PATHS = new Set(['/login', '/register', '/join', '/privacy', '/terms', '/extension']);

export function handleDefinitiveAuthFailure(): void {
  cancelScheduledRefreshRetry();
  useSessionHealthStore.getState().setDegraded(false);
  useAuthStore.getState().logout();
  if (typeof window !== 'undefined' && !PUBLIC_PATHS.has(window.location.pathname)) {
    window.location.href = '/login';
  }
}

/**
 * Thrown (and swallowed by every caller as a non-definitive failure) when a
 * refresh completes after the user has signed out.
 */
export class SessionEndedDuringRefreshError extends Error {
  constructor() {
    super('Session ended during token refresh');
    this.name = 'SessionEndedDuringRefreshError';
  }
}

// Bumped on every sign-out. Refresh work captures it when it starts and drops
// its result if it changed.
let sessionGeneration = 0;

/** Bumped on every logout; async work that captured an older value must not apply. */
export function getSessionGeneration(): number {
  return sessionGeneration;
}

export const REFRESH_RETRY_BASE_MS = 1000;
export const REFRESH_RETRY_MAX_MS = 30_000;
export const REFRESH_RETRY_MAX_ATTEMPTS = 10;
const DEGRADED_TOAST_ID = 'session-degraded';

let retryTimer: ReturnType<typeof setTimeout> | null = null;
let retryAttempt = 0;
// The success callback of the schedule in force, so a restart triggered by the
// browser's `online` event can resume the same recovery (e.g. the bootstrap
// hook's "fetch /users/me once a token is in hand").
let retryOnSuccess: (() => void) | undefined;

function markSessionHealthy(): void {
  // ANY successful refresh (an interceptor 401, a socket recovery, the bootstrap
  // hook) ends a pending recovery: consume its callback and cancel the queued
  // retry. Leaving the timer armed made it fire a redundant refresh that rotated
  // the cookie again and, if that extra call was rate-limited or failed, marked
  // an already healthy session degraded.
  const pendingRecovery = retryOnSuccess;
  cancelScheduledRefreshRetry();
  if (useSessionHealthStore.getState().degraded) {
    useSessionHealthStore.getState().setDegraded(false);
    toast.dismiss(DEGRADED_TOAST_ID);
  }
  pendingRecovery?.();
}

function markSessionDegraded(): void {
  if (useSessionHealthStore.getState().degraded) return;
  useSessionHealthStore.getState().setDegraded(true);
  toast.error("Can't reach ClinikChat right now — retrying in the background.", {
    id: DEGRADED_TOAST_ID,
    duration: 6000,
  });
}

// Once the fast schedule has given up, keep checking slowly: a server-only
// outage (429/5xx while the browser stays online) never fires `online`, and a
// bootstrap with no token has no other request to trigger a refresh.
export const REFRESH_EXHAUSTED_RECHECK_MS = 5 * 60_000;
let exhaustedTimer: ReturnType<typeof setTimeout> | null = null;

export function cancelScheduledRefreshRetry(): void {
  if (retryTimer) {
    clearTimeout(retryTimer);
    retryTimer = null;
  }
  if (exhaustedTimer) {
    clearTimeout(exhaustedTimer);
    exhaustedTimer = null;
  }
  retryAttempt = 0;
  retryOnSuccess = undefined;
}

/**
 * Keep the session alive across a transient refresh failure: mark the
 * session degraded and retry `/auth/refresh` with exponential backoff
 * (1s → 30s cap). A later definitive rejection still logs out; a success
 * clears the degraded flag and runs `onSuccess` (the bootstrap hook uses it
 * to fetch /users/me once a token is finally in hand).
 */
export function scheduleRefreshRetry(onSuccess?: () => void): void {
  if (typeof window === 'undefined') return;
  if (!useAuthStore.getState().hasSession) return;
  markSessionDegraded();
  if (onSuccess) retryOnSuccess = onSuccess;
  if (retryTimer) return;
  if (retryAttempt >= REFRESH_RETRY_MAX_ATTEMPTS) {
    // Out of attempts. The session is kept; the banner stops promising a
    // background retry. The `online` event, the tab becoming visible/focused
    // and a slow fallback timer all restart the schedule.
    useSessionHealthStore.getState().setRetriesExhausted(true);
    if (!exhaustedTimer) {
      exhaustedTimer = setTimeout(() => {
        exhaustedTimer = null;
        restartExhaustedRefreshRetry();
      }, REFRESH_EXHAUSTED_RECHECK_MS);
    }
    return;
  }
  const delay = Math.min(REFRESH_RETRY_BASE_MS * 2 ** retryAttempt, REFRESH_RETRY_MAX_MS);
  retryAttempt += 1;
  retryTimer = setTimeout(() => {
    retryTimer = null;
    refreshAccessTokenSingleFlight()
      .then(() => retryOnSuccess?.())
      .catch((err: unknown) => {
        if (isDefinitiveAuthFailure(err)) {
          handleDefinitiveAuthFailure();
          return;
        }
        scheduleRefreshRetry();
      });
  }, delay);
}

/**
 * The browser regained connectivity: if a refresh schedule was running (or
 * had run out of attempts), start it over immediately. Without this, once the
 * ten backoff attempts were spent during a long outage nothing would ever
 * refresh again on a page with no other request in flight (e.g. a bootstrap
 * with no access token), leaving it stuck until a manual reload.
 */
export function restartRefreshRetryOnReconnect(): void {
  if (!useAuthStore.getState().hasSession) return;
  const health = useSessionHealthStore.getState();
  if (!health.degraded && !health.retriesExhausted && !retryTimer) return;
  const onSuccess = retryOnSuccess;
  if (retryTimer) {
    clearTimeout(retryTimer);
    retryTimer = null;
  }
  retryAttempt = 0;
  health.setRetriesExhausted(false);
  scheduleRefreshRetry(onSuccess);
}

/** Restart ONLY a schedule that ran out of attempts (used by focus/visibility/slow timer). */
export function restartExhaustedRefreshRetry(): void {
  if (!useSessionHealthStore.getState().retriesExhausted) return;
  restartRefreshRetryOnReconnect();
}

if (typeof window !== 'undefined') {
  window.addEventListener('online', restartRefreshRetryOnReconnect);
  window.addEventListener('focus', restartExhaustedRefreshRetry);
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') restartExhaustedRefreshRetry();
  });
}

// Every logout path (the Sidebar button, an interceptor definitive failure, an
// account deletion) ends by clearing the auth store; observing THAT is what
// covers them all, instead of asking each path to remember to cancel. A queued
// retry timer is cancelled and in-flight refresh work is invalidated.
useAuthStore.subscribe((state, prev) => {
  if (prev.hasSession && !state.hasSession) {
    sessionGeneration += 1;
    refreshAbort?.abort();
    cancelScheduledRefreshRetry();
    if (useSessionHealthStore.getState().degraded) {
      useSessionHealthStore.getState().setDegraded(false);
      toast.dismiss(DEGRADED_TOAST_ID);
    }
  }
});

// ── Stale-bundle recovery (privacy policy version bump) ──────────────────

export const CONSENT_VERSION_STALE_CODE = 'CONSENT_VERSION_STALE';
const STALE_RELOAD_KEY = 'clinikchat:consent-stale-reload-at';
const STALE_RELOAD_COOLDOWN_MS = 60_000;

type MinimalStorage = Pick<Storage, 'getItem' | 'setItem'>;

/**
 * The API answers a consent POST carrying an outdated PRIVACY_POLICY_VERSION
 * with 409 CONSENT_VERSION_STALE: this bundle was compiled before the policy
 * bump and can never satisfy the gate. Reload once so the browser picks up
 * the current build; the sessionStorage timestamp stops a reload loop if the
 * served bundle is somehow still stale.
 */
export function shouldReloadForStaleConsent(
  storage: MinimalStorage | null,
  now: number = Date.now(),
): boolean {
  // Without storage the once-per-cooldown guard cannot be kept, so a reload
  // could loop; refuse rather than risk it (the update toast is the fallback).
  if (!storage) return false;
  const last = Number(storage.getItem(STALE_RELOAD_KEY) ?? 0);
  if (Number.isFinite(last) && last > 0 && now - last < STALE_RELOAD_COOLDOWN_MS) {
    return false;
  }
  storage.setItem(STALE_RELOAD_KEY, String(now));
  return true;
}

function safeSessionStorage(): MinimalStorage | null {
  try {
    return window.sessionStorage;
  } catch {
    return null;
  }
}

/**
 * The server rejected our compiled privacy-policy version: this bundle is
 * older than the deployed one. A plain reload does NOT fix that for an
 * installed PWA or any tab controlled by the prompt-mode service worker —
 * it answers the navigation from the OLD precache, so the reloaded page has
 * the same stale version and the user is stuck at the consent gate. Order:
 *  1. Activate a waiting worker (or check for one) — that reloads onto the
 *     new bundle.
 *  2. Still controlled by a worker with nothing to activate: a reload would
 *     be stale, so ask the user to apply the update explicitly.
 *  3. No worker in play (first visit, unsupported browser): a plain reload
 *     does fetch the new shell, guarded to once per cooldown.
 */
async function recoverFromStaleConsent(): Promise<void> {
  if (typeof window === 'undefined') return;
  try {
    if (await applyPendingUpdate()) return;
  } catch {
    /* fall through to the reload / prompt paths */
  }
  if (typeof navigator !== 'undefined' && navigator.serviceWorker?.controller) {
    showUpdateRequiredToast();
    return;
  }
  const storage = safeSessionStorage();
  if (!storage) {
    // The once-per-cooldown reload guard needs storage. Without it a silent
    // reload could loop and doing nothing leaves the user stuck at the
    // consent gate, so ask them to apply the update explicitly.
    showUpdateRequiredToast();
    return;
  }
  try {
    if (shouldReloadForStaleConsent(storage)) window.location.reload();
  } catch {
    // Storage that is exposed but throws on use (disabled by policy, quota
    // exhausted): the cooldown guard cannot be kept, so prompt instead.
    showUpdateRequiredToast();
  }
}

// ── Interceptors ─────────────────────────────────────────────────────────

api.interceptors.request.use((config) => {
  Object.assign(config.headers, CLIENT_HEADERS);
  const token = useAuthStore.getState().accessToken;
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

api.interceptors.response.use(
  (response) => response,
  async (error) => {
    // Named alias (rather than an inline parenthesized intersection in a
    // union) so this cast formats identically across prettier 3.8.x/3.9.x —
    // `(A & B) | undefined` as an inline union member gets wrapped onto
    // multiple lines by some patch versions and kept on one line by others,
    // which fights with the pre-commit hook's separately-pinned prettier
    // build. A plain identifier as the union member sidesteps that.
    type RetryableRequestConfig = typeof error.config & { _retry?: boolean };
    const originalRequest = error.config as RetryableRequestConfig | undefined;

    if (!originalRequest) {
      return Promise.reject(error);
    }

    // The server's minimum client version is above this bundle: show the
    // blocking "Update required" screen. Nothing else can succeed.
    if (reportIfUpdateRequired(error)) {
      return Promise.reject(error);
    }

    if (apiErrorCode(error) === CONSENT_VERSION_STALE_CODE) {
      void recoverFromStaleConsent();
      return Promise.reject(error);
    }

    if (
      error.response?.status === 401 &&
      !originalRequest._retry &&
      !String(originalRequest.url || '').includes('/api/v1/auth/refresh') &&
      !String(originalRequest.url || '').includes('/api/v1/auth/login') &&
      // A 401 here means "wrong TOTP/backup code" or "expired pending
      // session", not "stale access token" — there is no session yet to
      // refresh. Without this exclusion, a wrong MFA code during login
      // would trigger a doomed silent-refresh attempt (no refresh cookie
      // exists pre-login) and bounce the user out via the catch branch below.
      !String(originalRequest.url || '').includes('/api/v1/mfa/challenge')
    ) {
      originalRequest._retry = true;

      let newAccessToken: string;
      try {
        newAccessToken = await refreshAccessTokenSingleFlight();
      } catch (refreshError) {
        if (isDefinitiveAuthFailure(refreshError)) {
          handleDefinitiveAuthFailure();
        } else {
          // Offline / 429 / 5xx: the cookie is most likely still good. Keep
          // the session, tell the user, and retry the refresh in the
          // background. The caller sees its original 401 and handles it as
          // a failed request, not a lost session.
          scheduleRefreshRetry();
        }
        return Promise.reject(error);
      }

      originalRequest.headers = originalRequest.headers ?? {};
      originalRequest.headers.Authorization = `Bearer ${newAccessToken}`;
      return api(originalRequest);
    }

    return Promise.reject(error);
  },
);

export default api;
